import errorMessages, { errorCodes } from '../constants/errorMessages';

// The api is the authority on the Password Policy: these rules only give the Member instant
// feedback. They are written again in `api`, and both apps check them against the same test table.

export type PasswordPolicy = {
  minLength: number;
  requiredClasses: number;
  maxBytes: number;
}

export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  minLength: 14,
  requiredClasses: 3,
  maxBytes: 72,
};

export const passwordRuleCodes = [
  errorCodes.PASSWORD_TOO_SHORT,
  errorCodes.PASSWORD_TOO_LONG,
  errorCodes.PASSWORD_MISSING_CHARACTER_CLASSES,
  errorCodes.PASSWORD_CONTAINS_EMOJI,
] as const;

export type PasswordRuleCode = typeof passwordRuleCodes[number];

// Letters are classified by their Unicode case. Anything that isn't a cased letter or a digit,
// including spaces and letters from scripts without case, is "other".
const characterClassesRegexes = [/\p{Ll}/u, /\p{Lu}/u, /\p{Nd}/u, /[^\p{Ll}\p{Lu}\p{Nd}]/u];
const characterClassesDescription = 'lowercase letters, uppercase letters, digits, special characters';

// Emoji, plus the characters that build emoji sequences: zero-width joiner, variation selectors,
// skin-tone modifiers, the keycap combiner and the regional indicators that flags are made of.
const emoji = /\p{Extended_Pictographic}|\u200D|\uFE0E|\uFE0F|\u20E3|[\u{1F3FB}-\u{1F3FF}]|\p{Regional_Indicator}/u;

// bcrypt ignores everything after 72 bytes, so the maximum is measured in UTF-8 bytes.
const countBytes = (password: string) => new TextEncoder().encode(password).length;

const countCharacters = (password: string) => Array.from(password).length;

const countCharacterClasses = (password: string) => characterClassesRegexes
  .filter((characterClassRegex) => characterClassRegex.test(password))
  .length;

export const checkPasswordPolicy = (
  password: string,
  policy: PasswordPolicy,
): Record<PasswordRuleCode, boolean> => ({
  [errorCodes.PASSWORD_TOO_SHORT]: countCharacters(password) >= policy.minLength,
  [errorCodes.PASSWORD_TOO_LONG]: countBytes(password) <= policy.maxBytes,
  [errorCodes.PASSWORD_MISSING_CHARACTER_CLASSES]:
    countCharacterClasses(password) >= policy.requiredClasses,
  [errorCodes.PASSWORD_CONTAINS_EMOJI]: !emoji.test(password),
});

export const getFailedPasswordRules = (
  password: string,
  policy: PasswordPolicy,
): PasswordRuleCode[] => {
  const results = checkPasswordPolicy(password, policy);

  return passwordRuleCodes.filter((code) => !results[code]);
};

export const isPasswordPolicyMet = (password: string, policy: PasswordPolicy) => (
  getFailedPasswordRules(password, policy).length === 0
);

export const getPasswordRuleText = (code: string, policy: PasswordPolicy): string => {
  switch (code) {
    case errorCodes.PASSWORD_TOO_SHORT:
      return `At least ${policy.minLength} characters`;
    case errorCodes.PASSWORD_TOO_LONG:
      return `At most ${policy.maxBytes} bytes (accented letters and other scripts use more than one byte `
        + 'per character)';
    case errorCodes.PASSWORD_MISSING_CHARACTER_CLASSES: {
      const characterClassesLength = characterClassesRegexes.length;
      return policy.requiredClasses >= characterClassesLength
        ? `Characters from all ${characterClassesLength} types: ${characterClassesDescription}`
        : `Characters from at least ${policy.requiredClasses} of these ${characterClassesLength} types: ${characterClassesDescription}`;
    }
    case errorCodes.PASSWORD_CONTAINS_EMOJI:
      return 'No emoji';
    default:
      return errorMessages[errorCodes.PASSWORD_POLICY_VIOLATION];
  }
};

export const validatePasswordPolicy = (policy: PasswordPolicy) => (value?: string) => {
  const failed = getFailedPasswordRules(value ?? '', policy);

  if (failed.length === 0) {
    return undefined;
  }

  return failed.includes(errorCodes.PASSWORD_TOO_LONG)
    ? errorMessages[errorCodes.PASSWORD_TOO_LONG]
    : errorMessages[errorCodes.PASSWORD_POLICY_VIOLATION];
};
